import { Expose, instanceToPlain, plainToInstance, Transform } from 'class-transformer'
import type { PersonDetailResource as Model } from '@/services/api/client'
import type { CamelCaseKeys } from '@/types/utility'
import { map } from 'lodash'
import PeopleDetailProfileFieldResource from '@/services/api/transformers/PeopleDetailProfileFieldResource'
import PeopleDetailPersonDetailsResource from '@/services/api/transformers/PeopleDetailPersonDetailsResource'
import PeopleDetailFamilyMemberResource from '@/services/api/transformers/PeopleDetailFamilyMemberResource'
import PersonNoteResource from '@/services/api/transformers/PersonNoteResource'
import PeopleTagResource from '@/services/api/transformers/PeopleTagResource'
import type { PersonNavQuantitiesResource, PeopleTagResource as PeopleTagModel } from '@/services/api/client'

export type SerializedPersonDetailResource = Model & {
    tags?: PeopleTagModel[]
    // Returned by the API at the person's top level but absent from the generated
    // OpenAPI model; used to key conversations in the Messaging service.
    mds_id?: string
}

export default class PersonDetailResource implements CamelCaseKeys<SerializedPersonDetailResource> {
    id: Model['id']

    @Expose({ name: 'person_details' })
    @Transform(({ value }) => plainToInstance(PeopleDetailPersonDetailsResource, value), { toClassOnly: true })
    @Transform(({ value }) => instanceToPlain(value), { toPlainOnly: true })
    personDetails?: PeopleDetailPersonDetailsResource

    @Expose({ name: 'is_linked' })
    isLinked?: Model['is_linked']

    @Expose({ name: 'mds_id' })
    mdsId?: SerializedPersonDetailResource['mds_id']

    @Expose({ name: 'ministry_safe_id' })
    ministrySafeId?: Model['ministry_safe_id']

    @Expose({ name: 'profile_fields' })
    @Transform(({ value }) => map(value, item => plainToInstance(PeopleDetailProfileFieldResource, item)), {
        toClassOnly: true
    })
    @Transform(({ value }) => map(value, item => instanceToPlain(item)), { toPlainOnly: true })
    profileFields?: PeopleDetailProfileFieldResource[]

    @Expose({ name: 'family_members' })
    @Transform(({ value }) => map(value, item => plainToInstance(PeopleDetailFamilyMemberResource, item)), {
        toClassOnly: true
    })
    @Transform(({ value }) => map(value, item => instanceToPlain(item)), { toPlainOnly: true })
    familyMembers?: PeopleDetailFamilyMemberResource[]

    @Transform(({ value }) => map(value, item => plainToInstance(PersonNoteResource, item)), {
        toClassOnly: true
    })
    @Transform(({ value }) => map(value, item => instanceToPlain(item)), { toPlainOnly: true })
    notes?: PersonNoteResource[]

    @Expose({ name: 'nav_quantities' })
    navQuantities?: PersonNavQuantitiesResource

    @Expose({ name: 'is_in_same_family' })
    isInSameFamily?: boolean

    @Transform(({ value }) => (value ? map(value, item => plainToInstance(PeopleTagResource, item)) : []), {
        toClassOnly: true
    })
    @Transform(({ value }) => (value ? map(value, item => instanceToPlain(item)) : []), { toPlainOnly: true })
    tags?: PeopleTagResource[]

    constructor(values: PersonDetailResource) {
        Object.assign(this, values)
    }
}
